import React from "react";
import styled from "styled-components";
import { colors2, SkeletonLoader, TooltipV2 } from "oolib";
import {
  UI_BODY_SM,
  UI_BODY_SEMIBOLD_DF,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION_SEMIBOLD_DF,
  UI_TAG,
  UI_TAG_CONDENSED,
  UI_TITLE,
} from "oolib/dist/v2/components/Typo2";
import { ArrowUp, ArrowDown, Info } from "@phosphor-icons/react";
import { Bar, BarChart as RechartsBarChart, Cell, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { SidePanel } from "@/components/UI/SidePanel";
import { MentionCard } from "../../comps/MentionCard";
import { SimpleBarChart } from "../../comps/SimpleBarChart";
import { SentimentBar } from "./SentimentBar";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors2.white};
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`;

const FindingItem = styled.div`
  cursor: ${(p) => p.$clickable ? "pointer" : "default"};
  padding: 0.5rem 0.75rem;
  margin: 0 -0.75rem;
  border-radius: 0.5rem;
  &:hover {
    background: ${(p) => p.$clickable ? colors2.grey5 : "transparent"};
  }
`;

const Section = styled.section`
  padding: 20px 1.5rem;
  border-bottom: 1px solid ${colors2.grey5};

  &:last-child {
    border-bottom: none;
  }
`;

const HeaderTitleBlock = styled.span`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;
  min-width: 0;
  margin: -14px 0 -14px -14px;
  padding: 1.4rem 0 1.5rem 1.5rem;
  background: ${colors2.white};
  overflow: hidden;
`;

const HeaderGlow = styled.span`
  position: absolute;
  top: -180px;
  left: -50px;
  width: 151px;
  height: 151px;
  border-radius: 145px;
  background: ${(p) => p.$color};
  filter: blur(60px);
  pointer-events: none;
  z-index: 0;
`;

const HeaderContent = styled.span`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const HeaderTopRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const HeaderOverline = styled(UI_BODY_SEMIBOLD_SM_DF).attrs({ as: "span" })`
  color: ${(p) => p.$color};
  font-weight: 500;
  margin-bottom: calc(2px - 0.4rem);
`;

const HeaderTitle = styled(UI_TITLE)`
  color: ${(p) => p.$color};
  flex: 1;
  min-width: 0;
  margin-bottom: 16px;
`;

const CountRow = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  padding: 0.15rem 0;
`;

const CountValue = styled(UI_BODY_SEMIBOLD_DF)`
  color: ${(p) => p.$color};
`;

const CountLabel = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${(p) => p.$color};
  opacity: 0.7;
`;

function CountLine({ count, label, color }) {
  return (
    <CountRow>
      <CountValue $color={color}>{count}</CountValue>
      <CountLabel $color={color}>{label}</CountLabel>
    </CountRow>
  );
}

const Description = styled(UI_BODY_SM)`
  color: ${colors2.grey80};
  line-height: 1.55;
  margin-top: 1.1rem;
`;

export const SectionLabel = styled(UI_TAG)`
  color: ${colors2.grey50};
  display: block;
  margin-bottom: 0.85rem;
`;

const SourcesGroup = styled.div`
  margin-top: 1.5rem;
`;

// Sources label + optional (i) — carries the label's usual bottom margin so
// the plain SectionLabel elsewhere stays untouched.
const SectionLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
`;

const PolarityGroup = styled.div`
  margin-top: 1.5rem;
`;

const POLARITY_COLORS = {
  positive: "#57BE45",
  neutral:  "#03143033",
  negative: "#FF6A6A",
};

const TrendChartWrap = styled.div`
  *:focus {
    outline: none !important;
  }
`;

const ChartArea = styled.div`
  position: relative;
`;

const LabelOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding-bottom: 5px;
  pointer-events: none;
`;

const LabelSlot = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  color: ${colors2.grey70};
`;

const DotRow = styled.div`
  display: flex;
  margin-top: 0.5rem;
`;

const DotSlot = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const BarDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors2.grey40};
  opacity: ${(p) => (p.$active ? 1 : 0)};
`;

const TrendNote = styled(UI_BODY_SM)`
  color: ${colors2.grey60};
  line-height: 1.55;
  margin-top: 1rem;
`;

const LocalityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LocalityPill = styled.div`
  flex: ${(p) => p.$flex};
  padding: 0.7rem 1rem;
  border-radius: 0.5rem;
  background: ${colors2.grey10};
  color: ${colors2.grey80};
`;

const LocalitySpacer = styled.div`
  flex: ${(p) => p.$flex};
`;

const FindingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

function toTitleCase(s = "") {
  return s
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function localityArrow(direction) {
  if (direction === "up") return <ArrowUp size={18} weight="bold" color="#E84A3F" />;
  if (direction === "down") return <ArrowDown size={18} weight="bold" color="#2DA15F" />;
  return null;
}

export function TagDetailSidebar({
  isOpen,
  onClose,
  isLoading,
  width = 400,
  title,
  overline,
  mentionsCount,
  mentionsPeriod = "this month",
  description,
  sources,
  trendData,
  mentions,
  themes,
  findings,
  showSentimentPolarity,
  sentimentBreakdown,
  pill,
  primaryColor,
  glowColor,
  overrideHeaderTitle,
  // Optional plain-language explainer rendered as an (i) tooltip next to the
  // SOURCES label. String only — keep it one or two short sentences.
  sourcesInfo,
  onThemeBarClick,
  onFindingClick,
  rightOffset = 0,
  zIndex,
  covered,
  onUncover,
  peekWidth,
  closeOnOutsideClick = true,
  children,
}) {
  const normalizedTrend = (trendData || []).map((d) => ({
    label: (d.label || d.name || "").toUpperCase(),
    value: d.value ?? d.count ?? 0,
    active: d.active ?? d.isCurrentPeriod ?? false,
  }));
  const maxTrend = Math.max(...normalizedTrend.map((d) => d.value), 1);

  // Use full sentimentBreakdown when available (accurate), fall back to mentions sample
  const polarityCounts = sentimentBreakdown && (sentimentBreakdown.positive + sentimentBreakdown.negative + (sentimentBreakdown.neutral || 0)) > 0
    ? sentimentBreakdown
    : (mentions || []).reduce(
        (acc, m) => {
          const tone = ["positive", "negative", "neutral"].includes(m.tone) ? m.tone : "neutral";
          acc[tone] += 1;
          return acc;
        },
        { positive: 0, neutral: 0, negative: 0 }
      );
  const polarityTotal = (polarityCounts.positive || 0) + (polarityCounts.neutral || 0) + (polarityCounts.negative || 0);

  const hasStyledHeader = !!(primaryColor || glowColor);
  const headerTitle = overrideHeaderTitle ? (
    overrideHeaderTitle
  ) : hasStyledHeader ? (
    <HeaderTitleBlock>
      <HeaderGlow $color={glowColor} />
      <HeaderContent>
        {overline && <HeaderOverline $color={primaryColor}>{overline}</HeaderOverline>}
        <HeaderTopRow>
          {mentionsCount != null && (
            <CountLine
              count={mentionsCount}
              label={`Mentions ${mentionsPeriod}`}
              color={primaryColor}
            />
          )}
          {pill}
        </HeaderTopRow>
      </HeaderContent>
    </HeaderTitleBlock>
  ) : title;

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={isLoading ? "" : headerTitle}
      width={width}
      rightOffset={rightOffset}
      zIndex={zIndex}
      covered={covered}
      onUncover={onUncover}
      peekWidth={peekWidth}
      closeOnOutsideClick={closeOnOutsideClick}
      bodyStyle={{ padding: 0 }}
    >
    {isLoading ? (
      <SkeletonWrap>
        <SkeletonLoader style={{ height: 12, width: "40%", borderRadius: 4 }} />
        <SkeletonLoader style={{ height: 20, width: "75%", borderRadius: 4 }} />
        <SkeletonLoader style={{ height: 12, width: "55%", borderRadius: 4, marginTop: 8 }} />
        <SkeletonLoader style={{ height: 12, width: "90%", borderRadius: 4 }} />
        <SkeletonLoader style={{ height: 12, width: "80%", borderRadius: 4 }} />
        <SkeletonLoader style={{ height: 12, width: "65%", borderRadius: 4, marginTop: 8 }} />
        <SkeletonLoader style={{ height: 32, width: "100%", borderRadius: 6 }} />
        <SkeletonLoader style={{ height: 32, width: "100%", borderRadius: 6 }} />
        <SkeletonLoader style={{ height: 32, width: "100%", borderRadius: 6 }} />
      </SkeletonWrap>
    ) : (
    <Container>
      {(description || sources?.length > 0 || (showSentimentPolarity && polarityTotal > 0)) && (
        <Section style={{ paddingTop: 0 }}>
          {title && <HeaderTitle $color={primaryColor}>{title}</HeaderTitle>}
          {description && <Description style={{ marginTop: 0 }}>{description}</Description>}
          {sources?.length > 0 && (
            <SourcesGroup>
              <SectionLabelRow>
                <SectionLabel style={{ marginBottom: 0 }}>Sources</SectionLabel>
                {sourcesInfo && (
                  <TooltipV2
                    text={sourcesInfo}
                    placement="bottom-start"
                    showPointer
                    wrapperStyle={{ display: "inline-flex" }}
                  >
                    <Info
                      size={13}
                      color={colors2.grey40}
                      style={{ display: "block", cursor: "pointer" }}
                    />
                  </TooltipV2>
                )}
              </SectionLabelRow>
              {sources.map((s) => {
                const label = toTitleCase(s.display || s.label || s.collection || "");
                return (
                  <CountLine key={label} count={s.count} label={label} color={primaryColor} />
                );
              })}
            </SourcesGroup>
          )}
          {showSentimentPolarity && polarityTotal > 0 && (
            <PolarityGroup>
              <SectionLabel>Sentiment Polarity</SectionLabel>
              <SentimentBar
                height={10}
                roundedEnds
                segments={[
                  { key: "positive", value: polarityCounts.positive, color: POLARITY_COLORS.positive },
                  { key: "neutral", value: polarityCounts.neutral, color: POLARITY_COLORS.neutral },
                  { key: "negative", value: polarityCounts.negative, color: POLARITY_COLORS.negative },
                ]}
              />
            </PolarityGroup>
          )}
        </Section>
      )}

      {/* {trendData?.length > 0 && (
        <Section>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.85rem" }}>
            <SectionLabel style={{ marginBottom: 0 }}>Trend</SectionLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 4, color: colors2.grey50 }}>
              <UI_CAPTION_SEMIBOLD_DF>Current Month</UI_CAPTION_SEMIBOLD_DF>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#B19DFF", display: "inline-block" }} />
            </div>
          </div>
          <TrendChartWrap>
            <ChartArea>
            <ResponsiveContainer width="100%" height={120}>
              <RechartsBarChart
                data={normalizedTrend}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                barCategoryGap={2}
                barGap={4}
              >
                <XAxis dataKey="label" hide height={0} />
                <YAxis hide width={0} domain={[0, maxTrend]} />
                <Bar
                  dataKey="value"
                  fill="#0113311A"
                  radius={8}
                  isAnimationActive={false}
                  minPointSize={32}
                >
                  {normalizedTrend.map((d) => (
                    <Cell key={d.label} fill={d.active ? "#B19DFF" : "#0113311A"} />
                  ))}
                </Bar>
              </RechartsBarChart>
            </ResponsiveContainer>
            <LabelOverlay>
              {normalizedTrend.map((d) => (
                <LabelSlot key={d.label}>
                  <UI_TAG_CONDENSED>{d.label}</UI_TAG_CONDENSED>
                </LabelSlot>
              ))}
            </LabelOverlay>
            </ChartArea>
            <DotRow>
              {normalizedTrend.map((d) => (
                <DotSlot key={d.label}>
                  <BarDot $active={!!d.active} />
                </DotSlot>
              ))}
            </DotRow>
          </TrendChartWrap>
          {trendNote && <TrendNote>{trendNote}</TrendNote>}
        </Section>
      )} */}

      {/* {localities?.length > 0 && (
        <Section>
          <SectionLabel>Top Localities</SectionLabel>
          {localities.map((l) => (
            <LocalityRow key={l.label}>
              <LocalityPill $flex={l.weight ?? 1}>
                <UI_BODY_SM>{l.label}</UI_BODY_SM>
              </LocalityPill>
              <LocalitySpacer $flex={1 - (l.weight ?? 1)} />
              {localityArrow(l.direction)}
            </LocalityRow>
          ))}
        </Section>
      )} */}

      {themes && themes.length > 0 && (
        <Section>
          <SectionLabel>Co-occurring topics</SectionLabel>
          <SimpleBarChart
            data={themes.map((t) => ({ ...t, name: t.display, count: t.count }))}
            barColor={"#0113311A"}
            onClick={onThemeBarClick}
          />
        </Section>
      )}

      {findings && findings.length > 0 && (
        <Section>
          <SectionLabel>Findings in this signal</SectionLabel>
          <FindingsList>
            {findings.map((f, i) => (
              <FindingItem
                key={i}
                $clickable={!!onFindingClick && !!f.themeId}
                onClick={() => onFindingClick && f.themeId && onFindingClick({ id: f.themeId, display: f.themeDisplay, type: f.themeType })}
              >
                <UI_BODY_SEMIBOLD_DF>{f.text}</UI_BODY_SEMIBOLD_DF>
              </FindingItem>
            ))}
          </FindingsList>
        </Section>
      )}

      {mentions?.length > 0 && (
        <Section>
          <SectionLabel>Mentions</SectionLabel>
          {mentions.map((m, i) => (
            <MentionCard key={i} tone={m.tone} fragment={m.fragment}>{m.text}</MentionCard>
          ))}
        </Section>
      )}

      {/* Escape hatch for callers whose trailing content isn't themes /
          findings / mentions (e.g. the Topics tree's annotation cards).
          Caller supplies its own SectionLabel. */}
      {children && <Section>{children}</Section>}
    </Container>
    )}
    </SidePanel>
  );
}
